import React, { useEffect } from "react";
import Select from "../../common/select";
import Loader from "../../common/loader";
import Accordion from "../../common/accordion";
import TextInput from "../../common/textinput";
import { useAppSelector } from "../../../store/hooks/StoreHooks";
import useCollapseFacade from "../../../facade/collapse.Facade";
import useTubingFacade from "../../../facade/calculation/calculation-input/tubing.facade";
import LABELS from "../../../constants/labels/tubing.labels";

const Tubing: React.FC = () => {
  const { updateCasingCollapse, updateLinerCollapse, updateTubingCollapse } =
    useCollapseFacade();
  const {
    updateCasingOD,
    updateCasingWeight,
    updateLinerID,
    updateLinerOD,
    updateLinerWeight,
    updateLinerTop,
    updateTubingWeight,
    updateTubingOD,
    updateTubingType,
    updateTubing,
    clearCasing,
    casingWeightClear,
    linerODClear,
    tubginODClear,
    updateWellTrajectory,
  } = useTubingFacade();

  const { loading, tab1Input } = useAppSelector((state) => state?.tubing);
  const { casingCollapse, tubingCollapse, linerCollapse } = useAppSelector(
    (state) => state?.collapse
  );

  useEffect(() => {
    if (tab1Input?.tubingType === 0) {
      updateTubing("120");
    }
  }, [tab1Input]);

  const handleCasingODList = async (option: any) => {
    const selectedOD = option?.value;
    if (Number(selectedOD) === Number(tab1Input?.casingOD)) return false;
    if (selectedOD === "") {
      clearCasing(); // Clears casingOD and casingWeight presumably
    } else {
      updateCasingOD(selectedOD);      // Set new casingOD
      casingWeightClear();        // Reset casingWeight to null (like setValue(null))
    }
  };
  

  const handleCasingWeight = async (option: any) => {
    if (Number(option.value) === Number(tab1Input?.casingWeight)) return false;
    else if (option.value === "") casingWeightClear();
    else updateCasingWeight(option.value);
  };

  const handleLinerODList = async (option: any) => {
    if (Number(option.value) === Number(tab1Input?.linerOD)) return false;
    else updateLinerOD(option?.value);
  };

  const handleLinerWeight = async (option: any) => {
    if (Number(option.value) === Number(tab1Input?.linerWeight)) return false;
    else if (option.value === "") updateLinerID("");
    else updateLinerWeight(option.value);
  };

  const handleTubingOD = async (option: any) => {
    if (Number(option.value) === Number(tab1Input?.tubingOD)) return false;
    else if (option.value === "") tubginODClear();
    else updateTubingOD(option?.value);
  };

  const handleTubingTypeUpset = async (option: any) => {
    if (Number(option.value) === Number(tab1Input?.tubingTypeUpset))
      return false;
    else updateTubingType(option.value);
  };

  const handleTubingWeight = async (option: any) => {
    if (Number(option.value) === Number(tab1Input?.tubingWeight)) return false;
    else updateTubingWeight(option.value);
  };

  const handleTubing = (option: any) => {
    if (Number(option.value) === Number(tab1Input?.tubingType)) return false;
    else updateTubing(option.value);
  };

  const handleWellTrajectory = (option: any) => {
    if (Number(option.value) === Number(tab1Input?.wellTrajectory))
      return false;
    else updateWellTrajectory(option.value);
  };

  const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const { name, value } = e.target;
    const regex = /^[0-9]+(\.[0-9]*)?$/;
    if (value === "" || (name === "linerTop" && regex.test(value))) updateLinerTop(value);
  };

  const handleCollapseChange = (key: boolean, name: string) => {
    if (name === "casing") updateCasingCollapse(key);
    else if (name === "liner") updateLinerCollapse(key);
    else if (name === "tubing") updateTubingCollapse(key);
  };

  return (
    <>
      <Loader isLoading={loading} />
      <Accordion
        title={LABELS?.casing}
        isOpen={casingCollapse}
        onToggle={() => handleCollapseChange(!casingCollapse, "casing")}
        content={
          <div className="m-[12px]">
            <div className="mb-[16px]">
              <Select
                options={tab1Input?.casingODList}
                onChange={handleCasingODList}
                padding="px-[14px] sm:py-[10px] py-[5px]"
                fontSize="16px"
                className="sm:px-0 px-[10px]"
                required={true}
                selectedValue={tab1Input?.casingOD}
                label={LABELS?.casingOD}
              />
            </div>
            <div className="mb-[16px]">
              <Select
                options={tab1Input?.casingWeightList}
                onChange={handleCasingWeight}
                padding="px-[14px] sm:py-[10px] py-[5px]"
                fontSize="16px"
                className="sm:px-0 px-[10px]"
                selectedValue={tab1Input?.casingWeight}
                required={true}
                label={LABELS?.casingWeight}
              />
            </div>
            <div className="flex justify-between items-center my-[16px]">
              <label className="text-[16px] text-neutral-25">{LABELS?.casingID}</label>
              <label className="text-[16px] text-neutral-25">
                {tab1Input?.casingID} {tab1Input?.actNameCasingID || "in"}
              </label>
            </div>
            <div className="flex justify-between items-center my-[16px]">
              <label className="text-[16px] text-neutral-25">
                {LABELS?.casingDriftID}
              </label>
              <label className="text-[16px] text-neutral-25">
                {tab1Input?.casingDriftID} {tab1Input?.actNameCasingID || "in"}
              </label>
            </div>
          </div>
        }
      />
      <div className="mb-[16px]" />
      <Accordion
        title={LABELS?.liner}
        isOpen={linerCollapse}
        onToggle={() => handleCollapseChange(!linerCollapse, "liner")}
        content={
          <div className="m-[12px]">
            <div className="mb-[16px]">
              <Select
                options={tab1Input?.linerODList}
                onChange={handleLinerODList}
                padding="px-[14px] sm:py-[10px] py-[5px]"
                fontSize="16px"
                className="sm:px-0 px-[10px]"
                selectedValue={tab1Input?.linerOD}
                label={LABELS?.linerOD}
              />
            </div>
            <div className="mb-[16px]">
              <Select
                options={tab1Input?.linerWeightList}
                onChange={handleLinerWeight}
                padding="px-[14px] sm:py-[10px] py-[5px]"
                fontSize="16px"
                className="sm:px-0 px-[10px]"
                selectedValue={tab1Input?.linerWeight}
                label={LABELS?.linerWeight}
              />
            </div>
            <div className="flex justify-between items-center my-[16px]">
              <label className="text-[16px] text-neutral-25">{LABELS?.linerID}</label>
              <label className="text-[16px] text-neutral-25">
                {tab1Input?.linerID} {tab1Input?.actNameCasingID || "in"}
              </label>
            </div>
            <TextInput
              name="linerTop"
              value={tab1Input?.linerTop}
              onChange={handleOnChange}
              placeholder={""}
              label={LABELS?.linerTopMD}
              unit={tab1Input?.actNameLinerTop || "ft"}
              bgColor="bg-transparent"
            />
          </div>
        }
      />
      <div className="mb-[16px]" />
      <Accordion
        title={LABELS?.tubing}
        isOpen={tubingCollapse}
        onToggle={() => handleCollapseChange(!tubingCollapse, "tubing")}
        content={
          <div className="m-[12px]">
            <div className="mb-[16px]">
              <Select
                options={tab1Input?.tubingODList}
                onChange={handleTubingOD}
                padding="px-[14px] sm:py-[10px] py-[5px]"
                fontSize="16px"
                className="sm:px-0 px-[10px]"
                selectedValue={tab1Input?.tubingOD}
                label={LABELS?.tubingOD}
                required={true}
              />
            </div>
            <div className="mb-[16px]">
              <Select
                options={tab1Input?.tubingTypeUpsetList}
                onChange={handleTubingTypeUpset}
                padding="px-[14px] sm:py-[10px] py-[5px]"
                fontSize="16px"
                className="sm:px-0 px-[10px]"
                selectedValue={tab1Input?.tubingTypeUpset}
                label={LABELS?.tubingType}
                required={true}
              />
            </div>
            <div className="mb-[16px]">
              <Select
                options={tab1Input?.tubingWeightList}
                onChange={handleTubingWeight}
                padding="px-[14px] sm:py-[10px] py-[5px]"
                fontSize="16px"
                className="sm:px-0 px-[10px]"
                selectedValue={tab1Input?.tubingWeight}
                label={LABELS?.tubingWeight}
                required={true}
              />
            </div>
            <div className="flex justify-between items-center my-[16px]">
              <label className="text-[16px] text-neutral-25">{LABELS?.tubingID}</label>
              <label className="text-[16px] text-neutral-25">
                {tab1Input?.tubingID} {tab1Input?.actNameTubingID || "in"}
              </label>
            </div>
            <div className="mb-[16px]">
              <Select
                options={tab1Input?.tubingTypeList}
                onChange={handleTubing}
                padding="px-[14px] sm:py-[10px] py-[5px]"
                fontSize="16px"
                className="sm:px-0 px-[10px]"
                selectedValue={tab1Input?.tubingType || "120"}
                label={LABELS?.tubing}
                required={true}
              />
            </div>
          </div>
        }
      />
      <div className="mt-[15px]">
        <Select
          options={tab1Input?.wellTrajectoryList}
          onChange={handleWellTrajectory}
          padding="px-[14px] sm:py-[10px] py-[5px]"
          fontSize="16px"
          className="sm:px-0 px-[10px]"
          selectedValue={tab1Input?.wellTrajectory}
          label={LABELS?.wellTrajectory}
          dropdownDirection="up"
        />
      </div>
    </>
  );
};

export default Tubing;
